import { observer } from 'mobx-react'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useState } from 'react'
import { MyHead } from '@/components/molecules/MyHead'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { ForumTwoColumnContentTemplate } from '@/components/templates/ForumTwoColumnContentTemplate'
import { getStaticStores, useStores } from '@/utils'
import { IQuestionBase, Language, QuestionOrderBase } from '@/types'
import { Button } from '@/components/atoms/Button'
import { QuestionList } from '@/components/organisms/QuestionList'
import { ForumFormButton } from '@/components/molecules/ForumFormButton'
import { LoginToAnswerButton } from '@/components/molecules/LoginToAnswerButton'
import { KeywordSearchGroupForQuestions } from '@/components/organisms/KeywordSearchGroupForQuestions'
import { QuestionCategoriesMenuList } from '@/components/organisms/QuestionCategoriesMenuList'
import { ShowMoreOnTopForMb } from '@/components/organisms/ShowMoreOnTopForMb'
import { SortGroupForQuestions } from '@/components/organisms/SortGroupForQuestions'
import { FooterSnsLink } from '@/components/molecules/FooterSnsLink'
import { FooterSubMenu } from '@/components/molecules/FooterSubMenu'
import styles from './index.module.scss'

type Props = {
  staticQuestions: IQuestionBase[]
  staticHasNextQuestionsPage: boolean
  categorySlug: string
}

const NUM_OF_ITEMS = 24

const QuestionCategory: NextPage<Props> = observer(({ staticQuestions, staticHasNextQuestionsPage, categorySlug }) => {
  const useCategoryName = (slug: string): string => {
    if (slug === 'listing-fundraising') {
      return 'Listing/Fundraising'
    }
    if (slug === 'investment-airdrop') {
      return 'Investment/Airdrop'
    }
    if (slug === 'token-economy-de-fi') {
      return 'Token Economy/DeFi'
    }
    if (slug === 'kol-media') {
      return 'KOL/Media'
    }
    if (slug === 'engineering') {
      return 'Engineering'
    }
    if (slug === 'design') {
      return 'Design'
    }
    if (slug === 'tax-accounting') {
      return 'Tax/Accounting'
    }
    if (slug === 'legal') {
      return 'Legal'
    }
    if (slug === 'news') {
      return 'News'
    }

    // フォールバック
    return 'Other'
  }

  const { t } = useTranslation(['questions'])
  const categoryName = useCategoryName(categorySlug)
  const { viewer, questions } = useStores()
  // ISRで取得した配列より大きい場合、ページ送りの初回リクエストが実行されていることにする
  const hasDoneLoadMore = questions.categorizedQuestions[categorySlug]?.length > staticQuestions.length
  const [searchResults, setSearchResults] = useState<IQuestionBase[]>(
    hasDoneLoadMore ? questions.categorizedQuestions[categorySlug] : staticQuestions
  )
  const [searchKeyword, setSearchKeyword] = useState('')
  const [orderBy, setOrderBy] = useState<QuestionOrderBase>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [mounted, setMounted] = useState(false)

  const searchHandler = async (keyword: string, questionOrder: QuestionOrderBase) => {
    const result = await questions.fetchCategorizedQuestions({
      searchWord: keyword,
      orderBy: questionOrder,
      questionCategories: [categorySlug],
      limit: NUM_OF_ITEMS,
      shouldRefresh: true,
    })
    setSearchResults(result)
  }

  const fetchCategorizedQuestions = async () => {
    setIsFetching(true)
    await questions.fetchCategorizedQuestions({
      searchWord: searchKeyword,
      orderBy,
      questionCategories: [categorySlug],
      limit: hasDoneLoadMore ? NUM_OF_ITEMS : NUM_OF_ITEMS * 2,
      shouldRefresh: !hasDoneLoadMore,
    })
    setSearchResults(questions.categorizedQuestions[categorySlug])
    setIsFetching(false)
  }

  const setSearchValues = async (keyword: string, questionOrder: QuestionOrderBase) => {
    setSearchKeyword(keyword)
    setOrderBy(questionOrder)
    await searchHandler(keyword, questionOrder)
  }

  // カテゴリーが変わったときに質問を切り替える
  useEffect(() => {
    if (hasDoneLoadMore) {
      setSearchResults(questions.categorizedQuestions[categorySlug])
    } else {
      setSearchResults(staticQuestions)
    }
    questions.updateHasNextCategorizedQuestionsPage(categorySlug, staticHasNextQuestionsPage)
  }, [categorySlug])

  useEffect(() => {
    setMounted(true)
    // ブラウザ側での初期化時に Store に hasNextPage を設定
    questions.updateHasNextCategorizedQuestionsPage(categorySlug, staticHasNextQuestionsPage)
  }, [])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title='AQA Forum' description='Open to everyone for Web3 information exchange' />
      <ForumTwoColumnContentTemplate
        aside={
          <div className={styles.aside}>
            <KeywordSearchGroupForQuestions
              search={async (keyword, questionOrder) => setSearchValues(keyword, questionOrder)}
              orderBy={orderBy}
              setSearchKeyword={(word: string) => setSearchKeyword(word)}
            />
            <QuestionCategoriesMenuList />
            <FooterSubMenu />
            <div className={styles.sns}>
              <FooterSnsLink />
            </div>
          </div>
        }
      >
        <div className={styles.form}>{viewer.isSignedIn ? <ForumFormButton /> : <LoginToAnswerButton />}</div>
        <div className={styles.filter}>
          <KeywordSearchGroupForQuestions
            search={async (keyword, questionOrder) => setSearchValues(keyword, questionOrder)}
            orderBy={orderBy}
            setSearchKeyword={(word: string) => setSearchKeyword(word)}
          />
          <ShowMoreOnTopForMb />
        </div>
        <h2 className={styles.heading}>{categoryName}</h2>
        <SortGroupForQuestions
          search={async (keyword, questionOrder) => setSearchValues(keyword, questionOrder)}
          searchKeyword={searchKeyword}
          setOrderBy={(order: QuestionOrderBase) => setOrderBy(order)}
        />
        <QuestionList items={searchResults} />
        {mounted && questions.hasNextCategorizedQuestionsPage[categorySlug] && (
          <div className={styles.button}>
            <Button
              styleType='default'
              componentType='button'
              buttonSize='large'
              isLoading={isFetching}
              onClick={() => fetchCategorizedQuestions()}
            >
              {t('+ Load More')}
            </Button>
          </div>
        )}
      </ForumTwoColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { viewer, questions } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const categorySlug = params.category_slug as string
  const staticQuestions = await questions.fetchCategorizedQuestions({
    questionCategories: [categorySlug],
    limit: NUM_OF_ITEMS,
    shouldRefresh: true,
  })

  return {
    props: {
      staticQuestions,
      // JSON に undefined を代入するとビルドエラーが起きるので、null を使用する
      staticHasNextQuestionsPage: questions.hasNextCategorizedQuestionsPage[categorySlug] || null,
      categorySlug,
      ...(await serverSideTranslations(locale, ['global', 'validation', 'search', 'p'])),
    },
    revalidate: 1,
  }
}

// eslint-disable-next-line @typescript-eslint/require-await
export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const categories = [
    'listing-fundraising',
    'investment-airdrop',
    'token-economy-de-fi',
    'kol-media',
    'engineering',
    'design',
    'tax-accounting',
    'legal',
    'news',
  ]
  type Path = {
    params: { category_slug: string }
    locale: string
  }
  // pathの配列を作成
  const paths = locales.reduce((acc: Path[], locale: string) => {
    return [
      ...acc,
      ...categories.map((slug) => ({
        params: {
          category_slug: slug,
        },
        locale,
      })),
    ]
  }, [])

  return {
    paths,
    fallback: false,
  }
}

export default QuestionCategory
